import { Select } from "@mantine/core";
import { useFlowStore } from "../../../../../../../stores/WorkflowStore";
import { useLanguage } from "../../../../../../../stores/LanguageStore";
import { getEvents } from "./events";
import LinksSelector from "./LinksSelector";
import { useEffect, useMemo } from "react";
import EventName from "./EventName";
import { useAccountsStore } from "../../../../../../../stores/ConfigStore";
import { getNodeIdsOfChildren } from "../../components/NodeDeleter";

const EventSelector = ({
  id,
  event_type,
  event_name,
  nodeSource,
  which_links_clicked,
  sourceModel,
  goodEvent,
  messagesAreOnlyChildren,
  disabled,
}) => {
  const lang = useLanguage((s) => s.language);
  const workflowProfile = useFlowStore((s) => s.profile);
  const updateNodeData = useFlowStore((s) => s.updateNodeData);

  const handleEventChange = (newEvent) => {
    updateNodeData(id, { event_type: newEvent, name: null });
  };

  useEffect(() => {
    if (!goodEvent) {
      updateNodeData(id, {
        event_type: null,
        which_links_clicked: [],
        name: null,
      });
    }
  }, [goodEvent, updateNodeData, id]);

  const isError = useMemo(() => {
    if (nodeSource?.data?.actionType === "email") {
      const emails = useAccountsStore.getState().email_accounts;
      const email_pools = useAccountsStore.getState().email_pools;
      const integration = emails.find(
        (x) => x.id === nodeSource?.data?.integration_id,
      );
      const pool = email_pools.find((x) => x.id === nodeSource?.data?.pool_id);

      if (integration) {
        if (
          !integration?.enable_tracking &&
          ["link_click", "email_open", "email_open_no_click"].includes(
            event_type,
          )
        ) {
          return lang.workflows.nodes.no_tracking;
        }
        if (
          !integration?.can_read_responses &&
          event_type === "email_answered"
        ) {
          return lang.workflows.nodes.no_imap;
        }
      }

      if (pool) {
        if (
          !pool.accounts_list.find((x) => x?.enable_tracking) &&
          ["link_click", "email_open", "email_open_no_click"].includes(
            event_type,
          )
        ) {
          return lang.workflows.nodes.no_tracking;
        }
        if (
          !pool.accounts_list.find((x) => x?.can_read_responses) &&
          event_type === "email_answered"
        ) {
          return lang.workflows.nodes.no_imap;
        }
      }
    }

    return !event_type;
  }, [
    nodeSource?.data?.actionType,
    nodeSource?.data?.integration_id,
    nodeSource?.data?.pool_id,
    event_type,
    lang.workflows.nodes.no_tracking,
    lang.workflows.nodes.no_imap,
  ]);

  useEffect(() => {
    const updateNodeData = useFlowStore.getState().updateNodeData;
    if (!nodeSource?.data?.error) {
      const edges = useFlowStore.getState().edges;
      const nodes = useFlowStore.getState().nodes;
      let childrenIds = getNodeIdsOfChildren(edges, id, nodes);
      childrenIds.forEach((childId) => {
        updateNodeData(childId, { error: isError });
      });
    }
    if (nodeSource?.data?.actionType === "split_contacts") {
      updateNodeData(id, { event_type: "split_contacts", name: null });
    }
  }, [id, isError, nodeSource]);

  return nodeSource?.data?.actionType === "call" ? (
    <EventName event_name={event_name} goodEvent={goodEvent} id={id} />
  ) : (
    <Select
      value={goodEvent ? event_type : null}
      onChange={handleEventChange}
      size="sm"
      className="action-node nodrag nopan"
      disabled={disabled}
      placeholder={"Condition"}
      data={getEvents(
        nodeSource?.data?.actionType,
        !!workflowProfile?.auto_remove_responders ||
          !nodeSource?.data?.disable_auto_remove_responders,
        messagesAreOnlyChildren,
      )?.map((e) => ({
        ...e,
      }))}
      searchable
      maxDropdownHeight={200}
      comboboxProps={{
        size: "sm",
        width: 300
      }}
      rightSectionPointerEvents="all"
      rightSection={
        event_type === "link_click" && (
          <LinksSelector
            id={id}
            sourceModel={sourceModel}
            which_links_clicked={which_links_clicked}
          />
        )
      }
      variant="default"
      autoFocus={false}
      clearable={false}
      styles={{
        input: {
          border: isError ? "1px solid var(--mantine-color-red-6)" : "1px solid var(--mantine-color-primary-6)",
          background: "#fff",
        },
        root: {
          borderRadius: 12,
        },
      }}
    />
  );
};

export default EventSelector;
